import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const projectId = import.meta.env.VITE_SUPABASE_URL?.replace('https://', '').replace('.supabase.co', '') || '';
const publicAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export interface WebsiteSettings {
  siteName: string;
  siteNameArabic: string;
  logo: string;
  favicon: string;
  heroBackgroundImage: string;
  phone: string;
  email: string;
  address: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  facebookUrl: string;
  twitterUrl: string;
  instagramUrl: string;
  linkedinUrl: string;
  // Hero section settings
  heroSubtitle: string;
  heroTitle: string;
  heroWelcomeText: string;
  // About page specific settings
  aboutTitle: string;
  aboutSubtitle: string;
  aboutDescription: string;
  aboutMission: string;
  aboutVision: string;
  aboutValues: string;
  aboutTeamDescription: string;
}

const defaultSettings: WebsiteSettings = {
  siteName: 'Wageh Real Estate',
  siteNameArabic: ' Wageh Real Estate',
  logo: '',
  favicon: '',
  heroBackgroundImage: '',
  phone: '+20 123 456 789',
  email: 'info@wagehrealestate.com',
  address: 'Cairo, Egypt',
  description: 'Leading real estate platform in Egypt',
  metaTitle: 'Wageh Real Estate - Best Properties in Egypt',
  metaDescription: 'Discover the best properties for sale and rent in Egypt with Wageh Real Estate',
  facebookUrl: '',
  twitterUrl: '',
  instagramUrl: '',
  linkedinUrl: '',
  // Hero section defaults
  heroSubtitle: 'Discover the best properties for sale and rent in the area',
  heroTitle: 'Invest today in<br><span class="text-[#FFE5A0]">your dream home</span>',
  heroWelcomeText: 'Welcome to Wageh Real Estate',
  // About page defaults
  aboutTitle: 'Wageh Real Estate',
  aboutSubtitle: 'More than 10 years of experience in the real estate market, we help you find the right place for you with the best choice and least effort.',
  aboutDescription: 'We provide buying, selling, and renting real estate services (apartments - lands - buildings). We understand that choosing a property is not easy, so we focus on providing you with suitable options and we will be with you step by step until you reach the best choice for you.',
  aboutMission: 'We help you find the place that suits your needs and makes your investment successful.',
  aboutVision: 'To be the leader in the Egyptian real estate market by providing reliable services and meeting the needs of the changing market.',
  aboutValues: 'Trust - Excellence - Reliability - Professional Ethics - Commitment - Integration',
  aboutTeamDescription: 'A team of experts in the real estate market with over 10 years of experience'
};

interface SettingsContextType {
  settings: WebsiteSettings;
  updateSettings: (newSettings: Partial<WebsiteSettings>) => Promise<void>;
  loading: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const API_URL = import.meta.env.VITE_SUPABASE_URL ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/make-server-03e24050` : '';

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<WebsiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  // Fetch settings from Supabase on mount
  useEffect(() => {
    const fetchSettings = async () => {
      if (!API_URL) {
        console.warn('API_URL is not defined. Using default settings.');
        setSettings(defaultSettings);
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/settings`, {
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
          },
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          console.error('Settings fetch failed:', response.status, response.statusText, text);
          throw new Error(`Failed to fetch settings: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        if (data.settings) {
          setSettings(data.settings);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Fall back to default settings if fetch fails
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Update favicon and meta tags when settings change
  useEffect(() => {
    // Update favicon if set
    if (settings.favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      (link as HTMLLinkElement).type = 'image/x-icon';
      (link as HTMLLinkElement).rel = 'shortcut icon';
      (link as HTMLLinkElement).href = settings.favicon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    
    // Update page title
    if (settings.metaTitle) {
      document.title = settings.metaTitle;
    }
    
    // Update meta description
    const metaDescription = document.querySelector("meta[name='description']");
    if (metaDescription) {
      metaDescription.setAttribute('content', settings.metaDescription);
    }
  }, [settings]);

  const updateSettings = async (newSettings: Partial<WebsiteSettings>) => {
    if (!API_URL) {
      throw new Error('API_URL is not defined. Cannot save settings.');
    }
    const updatedSettings = { ...settings, ...newSettings };
    
    try {
      const response = await fetch(`${API_URL}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({ settings: updatedSettings }),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      const data = await response.json();
      if (data.settings) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      throw error;
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, loading }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
